import { ChangeEvent, useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import {
  FiatWalletAccountState,
  SupportedCurrency,
} from '__generated__/globalTypes';
import { RadioGroup } from 'atoms/inputs/RadioGroup';
import { Switch } from 'atoms/inputs/Switch';
import { SBHorizontal, Vertical } from 'atoms/layout/flex';
import { BodyM } from 'atoms/typography';
import { NeedsValidatedFiatWalletHelper } from 'components/fiatWallet/NeedsValidatedFiatWalletHelper';
import { useCurrentUserContext } from 'contexts/currentUser';
import { AcceptedCurrenciesValue } from 'hooks/useAcceptedCurrencies';
import { useCurrentUserAcceptedCurrencies } from 'hooks/useCurrentUserAcceptedCurrencies';
import { useReferenceCurrencyForSale } from 'hooks/useReferenceCurrencyForSale';
import { useFiatBalance } from 'hooks/wallets/useFiatBalance';
import { payment } from 'lib/glossary';
import { EnterCashWalletKYCFlow_Source } from 'protos/events/platform/web/events';

import { SettingsSection } from '../SettingsSection';
import { ConfirmationDialog } from './ConfirmationDialog';
import { messages } from './messages';

const OptionalRoot = styled.div`
  padding: 0 !important;
`;
const OptionalBlock = styled.div`
  padding: 0 var(--double-unit) var(--double-unit);
`;
const Optional = styled(SBHorizontal)`
  background-color: var(--c-nd-200);
  padding: var(--double-unit);
  border-radius: var(--unit);
`;

const Text = styled(Vertical).attrs({ gap: 0 })``;

const sectionMessages = defineMessages({
  title: {
    id: 'Settings.receivedPaymentCurrency.title',
    defaultMessage: 'How you’ll get paid',
  },
  description: {
    id: 'Settings.receivedPaymentCurrency.description',
    defaultMessage:
      'Decide how you’ll be paid for cards listed on the Marketplace as well as from direct offers from other Managers. <bold>This will only apply to future listings (current listings will follow previous payment settings)</bold>.',
  },
});

const Helper = ({ source }: { source: EnterCashWalletKYCFlow_Source }) => (
  <NeedsValidatedFiatWalletHelper
    withPadding
    source={source}
    statusTarget={FiatWalletAccountState.OWNER}
    defaultHelper={
      <FormattedMessage
        id="Settings.receivedPaymentCurrencies.cashAndEthHelper"
        defaultMessage="You’ll need to activate your Cash Wallet to receive payments in cash."
      />
    }
  />
);

export const AcceptedCurrencies = () => {
  const { canListAndTrade } = useFiatBalance();
  const {
    fiatCurrency: { code },
    walletPreferences: { showEthWallet, showFiatWallet },
  } = useCurrentUserContext();
  const { acceptedCurrencies, updateAcceptedCurrencies } =
    useCurrentUserAcceptedCurrencies();
  const [askForUpdateAcceptedCurrencies, setAskForUpdateAcceptedCurrencies] =
    useState<AcceptedCurrenciesValue | undefined>(undefined);
  const { referenceCurrencyForSale, update: updateReferenceCurrencyForSale } =
    useReferenceCurrencyForSale();

  const onReferenceCurrencyForSaleChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      updateAcceptedCurrencies(AcceptedCurrenciesValue.BOTH);
      setAskForUpdateAcceptedCurrencies(undefined);
    }
    updateReferenceCurrencyForSale(
      event.target.checked ? SupportedCurrency.WEI : code
    );
  };

  const onChange = (value: AcceptedCurrenciesValue) => {
    if (value === AcceptedCurrenciesValue.ETH) {
      setAskForUpdateAcceptedCurrencies(value);
    } else {
      updateAcceptedCurrencies(value);
      setAskForUpdateAcceptedCurrencies(undefined);
    }
  };
  const options = [
    {
      value: AcceptedCurrenciesValue.BOTH,
      label: (
        <BodyM color="var(--c-white)">
          <FormattedMessage {...messages.cashAndEth} />
        </BodyM>
      ),
      helper: (
        <Helper
          source={
            EnterCashWalletKYCFlow_Source.SETTINGS_CASH_AND_ETH_RECEIVED_PAYMENT_HELPER
          }
        />
      ),
      disabled: !canListAndTrade,
      optional: (
        <OptionalRoot>
          <OptionalBlock>
            <Optional>
              <Text>
                <BodyM bold color="var(--c-white)">
                  <FormattedMessage {...payment.peggingTitle} />
                </BodyM>
                <BodyM color="var(--c-nd-600)">
                  <FormattedMessage {...payment.peggingDescription} />
                </BodyM>
              </Text>
              <Switch
                name="reference-currency-for-sale"
                checked={
                  acceptedCurrencies === AcceptedCurrenciesValue.BOTH &&
                  referenceCurrencyForSale === SupportedCurrency.WEI
                }
                onChange={onReferenceCurrencyForSaleChange}
                $backgroundColor="var(--c-nd-400)"
              />
            </Optional>
          </OptionalBlock>
        </OptionalRoot>
      ),
    },
    {
      value: AcceptedCurrenciesValue.FIAT,
      label: (
        <BodyM color="var(--c-white)">
          <FormattedMessage {...messages.fiat} />
        </BodyM>
      ),
      helper: (
        <Helper
          source={
            EnterCashWalletKYCFlow_Source.SETTINGS_CASH_ONLY_RECEIVED_PAYMENT_HELPER
          }
        />
      ),
      disabled: !canListAndTrade,
    },
    {
      value: AcceptedCurrenciesValue.ETH,
      label: (
        <BodyM color="var(--c-white)">
          <FormattedMessage {...messages.eth} />
        </BodyM>
      ),
    },
  ];

  if (!showEthWallet || !showFiatWallet) return null;

  return (
    <SettingsSection {...sectionMessages}>
      <RadioGroup
        withSpacing
        options={options}
        value={acceptedCurrencies || AcceptedCurrenciesValue.ETH}
        name="currency"
        onChange={onChange}
      />

      <ConfirmationDialog
        open={
          !!askForUpdateAcceptedCurrencies &&
          askForUpdateAcceptedCurrencies !== acceptedCurrencies
        }
        value={askForUpdateAcceptedCurrencies}
        onConfirm={() => {
          updateAcceptedCurrencies(askForUpdateAcceptedCurrencies!);
        }}
        onCancel={() => {
          setAskForUpdateAcceptedCurrencies(acceptedCurrencies);
        }}
      />
    </SettingsSection>
  );
};
